import { APIClient, ApiQueryParams, DefaultQueryParams } from '@frontend/api-utils';
import { CreateUser, IUser, UpdateUser, UserListResponse, UserQueryParams } from '@frontend/user/types';

export class UserClient extends APIClient {
    public static readonly endpoint = '/user-api/v1';

    public static async fetchMyself(): Promise<IUser> {
        const response = await this.fetch(`${this.endpoint}/myself`);
        return await this.handleResponse<IUser>(response);
    }

    public static async fetchUsers(queryParams?: ApiQueryParams<UserQueryParams>): Promise<UserListResponse> {
        return await this.apiPaginated<UserListResponse, UserQueryParams>(`${this.endpoint}/users`, queryParams);
    }

    public static async postUser(accountId: string, user: CreateUser): Promise<IUser> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/users`, user);
        return await this.handleResponse<IUser>(response);
    }

    public static async fetchUsersNotInGroup(
        accountId: string,
        groupId: string,
        queryParams?: ApiQueryParams<DefaultQueryParams | UserQueryParams>
    ): Promise<UserListResponse> {
        return await this.apiPaginated<UserListResponse, DefaultQueryParams | UserQueryParams>(
            `${this.endpoint}/accounts/${accountId}/groups/${groupId}/users`,
            queryParams
        );
    }

    public static async fetchUser(accountId: string, userId: string): Promise<IUser> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/users/${userId}`);
        return await this.handleResponse<IUser>(response);
    }

    public static async fetchSupervisedUsers(
        accountId: string,
        userId: string,
        queryParams?: ApiQueryParams<DefaultQueryParams | UserQueryParams>
    ): Promise<UserListResponse> {
        return await this.apiPaginated<UserListResponse, DefaultQueryParams | UserQueryParams>(
            `${this.endpoint}/accounts/${accountId}/users/${userId}/supervised`,
            queryParams
        );
    }

    public static async patchUser(accountId: string, userId: string, user: UpdateUser): Promise<IUser> {
        const response = await this.patch(`${this.endpoint}/accounts/${accountId}/users/${userId}`, user);
        return await this.handleResponse<IUser>(response);
    }

    public static async deleteUser(accountId: string, userId: string): Promise<void> {
        const response = await this.delete(`${this.endpoint}/accounts/${accountId}/users/${userId}`);
        return await this.handleVoidResponse(response);
    }

    public static async resolveUsers(ids: string[], field: string = 'id'): Promise<UserListResponse> {
        return this.resolve<UserListResponse>(`${this.endpoint}/users`, ids, field);
    }

    public static async deleteUsers(userIds: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/users-delete`, { ids: userIds });
        return await this.handleVoidResponse(response);
    }

    //Profile

    public static async getProfile(): Promise<IUser> {
        const response = await this.fetch(`${this.endpoint}/profile`);
        return await this.handleResponse<IUser>(response);
    }

    public static async updateTokenUser(user: UpdateUser): Promise<IUser> {
        const response = await this.patch(`${this.endpoint}/profile`, user);
        return await this.handleResponse<IUser>(response);
    }

    public static async postIoTSeed(accountId: string, iotId: string, body: { account_id: string }): Promise<void> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/iots/${iotId}/seed`, body);
        return this.handleResponse(response);
    }
}
